import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useRouteError } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import type { Permission } from '@marketing-os/shared';
import { useAuth } from '@/stores/auth.store';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

/** Exige sessão ativa; redireciona para o login preservando o destino. */
export function ProtectedRoute({ children }: { children?: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

/** Envia o usuário de portal para a área correta e vice-versa. */
export function PortalRedirect({ children }: { children?: ReactNode }) {
  const { isPortalUser } = useAuth();
  if (isPortalUser) return <Navigate to="/portal" replace />;
  return <>{children ?? <Outlet />}</>;
}

export interface RequirePermissionProps {
  permission: Permission | Permission[];
  children?: ReactNode;
  /** Quando true, exige TODAS as permissões da lista. */
  all?: boolean;
}

export function RequirePermission({ permission, children, all = false }: RequirePermissionProps) {
  const { can, canAll } = useAuth();
  const list = Array.isArray(permission) ? permission : [permission];
  const allowed = all ? canAll(list) : can(list);

  if (!allowed) return <AccessDenied />;
  return <>{children ?? <Outlet />}</>;
}

export function AccessDenied() {
  return (
    <div className="surface-card">
      <EmptyState
        title="Acesso não autorizado"
        description="Seu perfil não tem permissão para acessar esta área. Fale com o administrador da agência se precisar de acesso."
        icon={<ShieldAlert className="h-6 w-6 text-warning" />}
        action={
          <Button variant="outline" size="sm" onClick={() => window.history.back()}>
            Voltar
          </Button>
        }
      />
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="surface-card">
      <EmptyState
        title="Página não encontrada"
        description="O endereço acessado não existe ou foi movido."
        action={
          <Button variant="outline" size="sm" onClick={() => window.location.assign('/')}>
            Ir para o início
          </Button>
        }
      />
    </div>
  );
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const message =
    error instanceof Error ? error.message : 'Ocorreu um erro inesperado ao renderizar esta página.';

  return (
    <div className="p-6">
      <div className="surface-card">
        <EmptyState
          title="Algo deu errado"
          description={message}
          icon={<ShieldAlert className="h-6 w-6 text-danger" />}
          action={
            <Button size="sm" onClick={() => window.location.reload()}>
              Recarregar
            </Button>
          }
        />
      </div>
    </div>
  );
}
